import { useState, useEffect, FormEvent, ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Check, Settings } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type Country = {
  id: number
  code: string
  name: string
  currency_code: string
  currency_symbol: string
  timezone: string
  is_active: boolean
  default_vat_rate: string | null
  locale_code: string
}

type CountryInput = Omit<Country, 'id'>

type DataSource = {
  api_url: string
  provider: 'rest_countries' | 'custom'
  last_imported_at: string | null
  last_import_count: number
}

const emptyCountry: CountryInput = {
  code: '',
  name: '',
  currency_code: '',
  currency_symbol: '',
  timezone: '',
  is_active: true,
  default_vat_rate: null,
  locale_code: '',
}

type TabKey = 'countries' | 'api_import'

export default function CountriesList() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<TabKey>('countries')

  const countriesQ = useQuery({
    queryKey: ['countries'],
    queryFn: async () => (await api.get('/countries/')).data,
  })

  const sourceQ = useQuery({
    queryKey: ['country-data-source'],
    queryFn: async () => (await api.get<DataSource>('/countries/data-source/')).data,
  })

  const countries = Array.isArray(countriesQ.data)
    ? countriesQ.data
    : countriesQ.data?.results || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-foreground">Countries</h2>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {([
            ['countries', `Countries (${countries.length})`],
            ['api_import', 'API Import'],
          ] as [TabKey, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === key
                  ? 'border-blue-600 text-blue-700 font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'countries' && (
        <CountriesTable
          countries={countries}
          isLoading={countriesQ.isLoading}
        />
      )}

      {tab === 'api_import' && (
        <ApiSection
          data={sourceQ.data}
          isLoading={sourceQ.isLoading}
          onImported={() => {
            qc.invalidateQueries({ queryKey: ['countries'] })
            qc.invalidateQueries({ queryKey: ['country-data-source'] })
            qc.invalidateQueries({ queryKey: ['countries', 'count'] })
            setTab('countries')
          }}
        />
      )}
    </AdminLayout>
  )
}

// ────────────────────────────────────────────────────
// API Country Data section
// ────────────────────────────────────────────────────

function ApiSection({
  data,
  isLoading,
  onImported,
}: {
  data: DataSource | undefined
  isLoading: boolean
  onImported: () => void
}) {
  const qc = useQueryClient()
  const [apiUrl, setApiUrl] = useState('')
  const [provider, setProvider] = useState<DataSource['provider']>('rest_countries')
  const [overwrite, setOverwrite] = useState(false)
  const [savedFlag, setSavedFlag] = useState(false)

  useEffect(() => {
    if (data) {
      setApiUrl(data.api_url)
      setProvider(data.provider)
    }
  }, [data])

  const saveMut = useMutation({
    mutationFn: async (payload: { api_url: string; provider: string }) =>
      (await api.patch<DataSource>('/countries/data-source/', payload)).data,
    onSuccess: (updated) => {
      qc.setQueryData(['country-data-source'], updated)
      setSavedFlag(true)
      setTimeout(() => setSavedFlag(false), 2000)
    },
  })

  const importMut = useMutation({
    mutationFn: async () =>
      (await api.post('/countries/import/', { overwrite })).data,
    onSuccess: () => {
      onImported()
    },
  })

  return (
    <div className="max-w-2xl">
      <div className="flex items-start justify-between mb-6">
        <p className="text-sm text-muted-foreground">
          Bulk-import country info from a public API. Default is{' '}
          <a
            href="https://restcountries.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            REST Countries
          </a>{' '}
          (free, no auth). You can swap the URL if the provider becomes unavailable.
        </p>
        {data?.last_imported_at && (
          <div className="text-xs text-muted-foreground text-right shrink-0 ml-6">
            Last imported<br />
            <span className="font-medium text-foreground">
              {new Date(data.last_imported_at).toLocaleString()}
            </span>
            <br />
            ({data.last_import_count} rows)
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="bg-card rounded-lg shadow p-6">
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="ds-provider">Provider</FieldLabel>
                <Select
                  value={provider}
                  onValueChange={(v) => setProvider(v as DataSource['provider'])}
                >
                  <SelectTrigger id="ds-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rest_countries">REST Countries (restcountries.com)</SelectItem>
                    <SelectItem value="custom">Custom JSON endpoint</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel htmlFor="ds-api-url">API endpoint URL</FieldLabel>
                <Input
                  id="ds-api-url"
                  type="url"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  className="font-mono text-xs"
                  placeholder="https://restcountries.com/v3.1/all?fields=name,cca2,currencies,timezones"
                />
                <FieldDescription>
                  For REST Countries provider, the response is parsed into code / name / currency /
                  timezone automatically.
                </FieldDescription>
              </Field>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                />
                <span className="text-sm text-foreground">
                  Overwrite existing rows (default: keep manual edits)
                </span>
              </label>
            </FieldGroup>

            <div className="flex items-center space-x-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => saveMut.mutate({ api_url: apiUrl, provider })}
                disabled={saveMut.isPending}
              >
                {saveMut.isPending ? 'Saving…' : 'Save endpoint'}
              </Button>
              <Button
                type="button"
                onClick={() => importMut.mutate()}
                disabled={importMut.isPending}
              >
                {importMut.isPending ? 'Importing…' : 'Import countries from API'}
              </Button>
              {savedFlag && (
                <span className="text-green-600 text-sm inline-flex items-center gap-1">
                  <Check className="h-4 w-4" /> Endpoint saved
                </span>
              )}
            </div>

            {importMut.isError && (
              <div className="text-red-700 text-sm bg-red-50 px-3 py-2 rounded mt-2">
                {String(
                  (importMut.error as any)?.response?.data?.detail ||
                    (importMut.error as any)?.message ||
                    importMut.error
                )}
              </div>
            )}

            {importMut.isSuccess && (
              <div className="text-sm bg-green-50 text-green-800 px-3 py-2 rounded mt-2">
                <Check className="inline h-4 w-4 mr-1 align-text-bottom" />Imported. Created {(importMut.data as any).created}, updated{' '}
                {(importMut.data as any).updated}, skipped {(importMut.data as any).skipped} of{' '}
                {(importMut.data as any).total_fetched} fetched.
              </div>
            )}
          </FieldSet>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────
// Countries table
// ────────────────────────────────────────────────────

function CountriesTable({ countries, isLoading }: { countries: Country[]; isLoading: boolean }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Country | null>(null)
  const [creating, setCreating] = useState(false)

  // Pagination + search state
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)

  const filtered = search
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.code.toLowerCase().includes(search.toLowerCase()) ||
          c.currency_code.toLowerCase().includes(search.toLowerCase())
      )
    : countries

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)

  const createMut = useMutation({
    mutationFn: async (payload: CountryInput) => (await api.post('/countries/', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['countries'] })
      qc.invalidateQueries({ queryKey: ['countries', 'count'] })
      setCreating(false)
    },
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: CountryInput }) =>
      (await api.put(`/countries/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['countries'] })
      setEditing(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/countries/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['countries'] })
      qc.invalidateQueries({ queryKey: ['countries', 'count'] })
    },
  })

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-gray-800">
          Configured countries ({filtered.length}{filtered.length !== countries.length && ` of ${countries.length}`})
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="text"
            placeholder="Search code, name, currency…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="w-64"
          />
          <Select
            value={String(pageSize)}
            onValueChange={(v) => { setPageSize(Number(v)); setPage(1) }}
          >
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setCreating(true)}>+ Add manually</Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : countries.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">
            No countries yet. Use the API import above or add one manually.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Currency</Th>
                <Th>Timezone</Th>
                <Th>VAT</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pageRows.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <Td className="font-mono">
                    <Link to={`/admin/countries/${c.id}`} className="text-primary hover:underline">{c.code}</Link>
                  </Td>
                  <Td>
                    <Link to={`/admin/countries/${c.id}`} className="hover:underline">{c.name}</Link>
                  </Td>
                  <Td>
                    {c.currency_code}{' '}
                    {c.currency_symbol && (
                      <span className="text-gray-500">({c.currency_symbol})</span>
                    )}
                  </Td>
                  <Td className="text-gray-600 text-xs">{c.timezone}</Td>
                  <Td>{c.default_vat_rate ? `${c.default_vat_rate}%` : '—'}</Td>
                  <Td>
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        c.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  </Td>
                  <Td>
                    <Link to={`/admin/countries/${c.id}`}
                      className="text-primary hover:underline text-sm mr-3 inline-flex items-center gap-1">
                      <Settings className="h-3.5 w-3.5" /> Configure
                    </Link>
                    <button
                      onClick={() => setEditing(c)}
                      className="text-blue-600 hover:underline text-sm mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete ${c.name}?`)) deleteMut.mutate(c.id)
                      }}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
              <div>
                Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={safePage === 1}
                  className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30">«</button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}
                  className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30">‹</button>
                <span className="px-2">Page {safePage} / {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                  className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30">›</button>
                <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages}
                  className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30">»</button>
              </div>
            </div>
          )}
        </div>
      )}

      {(creating || editing) && (
        <CountryDialog
          initial={editing || emptyCountry}
          onCancel={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSubmit={(payload) => {
            if (editing) updateMut.mutate({ id: editing.id, payload })
            else createMut.mutate(payload)
          }}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
        />
      )}
    </>
  )
}

function CountryDialog({
  initial,
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  initial: CountryInput
  onSubmit: (payload: CountryInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<CountryInput>(initial)
  const set = <K extends keyof CountryInput>(k: K, v: CountryInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  const isEdit = 'id' in (initial as any)

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {isEdit ? 'Edit country' : 'Add country'}
        </h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="country-code">Code (ISO 2)</FieldLabel>
                  <Input
                    id="country-code"
                    type="text"
                    required
                    maxLength={2}
                    value={form.code}
                    onChange={(e) => set('code', e.target.value.toUpperCase())}
                    placeholder="RO"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="country-currency-code">Currency (ISO 3)</FieldLabel>
                  <Input
                    id="country-currency-code"
                    type="text"
                    required
                    maxLength={3}
                    value={form.currency_code}
                    onChange={(e) => set('currency_code', e.target.value.toUpperCase())}
                    placeholder="RON"
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="country-name">Name</FieldLabel>
                <Input
                  id="country-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Romania"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="country-currency-symbol">Currency symbol</FieldLabel>
                  <Input
                    id="country-currency-symbol"
                    type="text"
                    value={form.currency_symbol}
                    onChange={(e) => set('currency_symbol', e.target.value)}
                    placeholder="lei"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="country-vat">Default VAT %</FieldLabel>
                  <Input
                    id="country-vat"
                    type="number"
                    step="0.01"
                    value={form.default_vat_rate || ''}
                    onChange={(e) => set('default_vat_rate', e.target.value || null)}
                    placeholder="19.00"
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="country-timezone">Timezone (IANA)</FieldLabel>
                <Input
                  id="country-timezone"
                  type="text"
                  required
                  value={form.timezone}
                  onChange={(e) => set('timezone', e.target.value)}
                  placeholder="Europe/Bucharest"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="country-locale">Locale (BCP-47)</FieldLabel>
                <Input
                  id="country-locale"
                  type="text"
                  value={form.locale_code}
                  onChange={(e) => set('locale_code', e.target.value)}
                  placeholder="ro-RO"
                />
              </Field>
              <label className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(
                error?.response?.data
                  ? JSON.stringify(error.response.data)
                  : error.message || error
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
      {children}
    </th>
  )
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
